import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { PipelineData } from '@/lib/pipeline';
import { euro } from './pipelineFormat';

/**
 * Kop van het pipelinescherm: waar staat het geld, wat is er deze maand
 * binnengekomen, en de drie cijfers waar een salesmanager op stuurt.
 *
 * Alle bedragen komen uit de investerings- en prijsblokken van de offerte zelf,
 * hetzelfde bedrag dat de klant op de PDF ziet. Offertes zonder bedragblokken
 * tellen als 0: die zijn wel zichtbaar in de aantallen, niet in de waarde.
 */

interface SegmentDefinition {
  label: string;
  valueCents: number;
  /** Hex uit badge.tsx, zodat een balksegment dezelfde kleur heeft als de badge van die status. */
  color: string;
}

function ValueBar({ segments }: { segments: SegmentDefinition[] }) {
  const total = segments.reduce((sum, segment) => sum + segment.valueCents, 0);
  const visible = segments.filter((segment) => segment.valueCents > 0);

  return (
    <div className="space-y-2.5">
      <div className="flex h-1.5 w-full overflow-hidden rounded bg-muted">
        {total > 0 &&
          visible.map((segment) => (
            <div
              key={segment.label}
              style={{
                width: `${(segment.valueCents / total) * 100}%`,
                backgroundColor: segment.color,
              }}
            />
          ))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        {segments.map((segment) => (
          <div key={segment.label} className="flex items-start gap-1.5">
            <span
              className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: segment.color }}
            />
            <div className="leading-tight">
              <div className="text-xs text-muted-foreground">{segment.label}</div>
              <div className="text-xs font-semibold tabular-nums">{euro(segment.valueCents)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthlyBars({ data }: { data: PipelineData }) {
  const months = data.monthlyAccepted;
  const peak = Math.max(...months.map((month) => month.valueCents), 1);
  const previousLabel = months.length > 1 ? months[months.length - 2].label : null;

  const delta = useMemo(() => {
    const previous = data.acceptedPreviousMonthValueCents;
    if (!previous) return null;
    const change = ((data.acceptedThisMonthValueCents - previous) / previous) * 100;
    return Math.round(change);
  }, [data.acceptedPreviousMonthValueCents, data.acceptedThisMonthValueCents]);

  return (
    <div className="space-y-2">
      <div className="flex h-9 items-end gap-1.5">
        {months.map((month, index) => (
          <div
            key={month.key}
            title={`${month.label}: ${euro(month.valueCents)}`}
            className={cn(
              'flex-1 rounded-sm',
              index === months.length - 1 ? 'bg-[#00856a]' : 'bg-[#cbd6e2]',
            )}
            style={{
              height: `${Math.max(4, Math.round((month.valueCents / peak) * 36))}px`,
            }}
          />
        ))}
      </div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          Geaccepteerde waarde per maand
        </span>
        {delta !== null && previousLabel && (
          <span
            className={cn(
              'text-xs font-semibold',
              delta >= 0 ? 'text-[#00856a]' : 'text-[#d94c53]',
            )}
          >
            {delta >= 0 ? '+' : ''}
            {delta}% t.o.v. {previousLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function StatRow({
  label,
  hint,
  value,
  tone = 'default',
}: {
  label: string;
  hint: string;
  value: string;
  tone?: 'default' | 'warning';
}) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4">
      <div className="min-w-0">
        <div
          className={cn(
            'truncate text-sm font-medium',
            tone === 'warning' && 'text-[#b8600b]',
          )}
        >
          {label}
        </div>
        <div className="truncate text-xs text-muted-foreground">{hint}</div>
      </div>
      <div
        className={cn(
          'shrink-0 text-2xl font-bold tabular-nums tracking-tight',
          tone === 'warning' && 'text-[#b8600b]',
        )}
      >
        {value}
      </div>
    </div>
  );
}

export function PipelineSummary({ data }: { data: PipelineData }) {
  const stageValue = (id: string) =>
    data.stages.find((stage) => stage.id === id)?.valueCents ?? 0;

  const expiring = data.signals.find((signal) => signal.id === 'verloopt');

  return (
    <Card className="flex flex-col xl:flex-row">
      <section className="flex-1 space-y-3 border-b p-5 xl:border-b-0 xl:border-r">
        <div className="text-sm font-medium">Open waarde</div>
        <div className="text-3xl font-bold tabular-nums tracking-tight">
          {euro(data.openValueCents)}
        </div>
        <div className="text-xs text-muted-foreground">
          {data.openCount} {data.openCount === 1 ? 'offerte loopt' : 'offertes lopen'} nog
        </div>
        <ValueBar
          segments={[
            { label: 'Gepubliceerd', valueCents: stageValue('gepubliceerd'), color: '#0091ae' },
            { label: 'Bekeken', valueCents: stageValue('bekeken'), color: '#2e475d' },
            { label: 'Ondertekening', valueCents: stageValue('ondertekening'), color: '#b8600b' },
          ]}
        />
        {data.riskCount > 0 && (
          <p className="border-t pt-3 text-xs text-muted-foreground">
            Daarnaast {data.riskCount === 1 ? 'is' : 'zijn'} er{' '}
            <span className="font-semibold text-[#d94c53]">
              {data.riskCount} verlopen {data.riskCount === 1 ? 'offerte' : 'offertes'}
            </span>{' '}
            met {euro(data.riskValueCents)} aan waarde die niet meer meetelt.
          </p>
        )}
      </section>

      <section className="flex-1 space-y-3 border-b p-5 xl:border-b-0 xl:border-r">
        <div className="text-sm font-medium">Getekend deze maand</div>
        <div className="text-3xl font-bold tabular-nums tracking-tight text-[#00856a]">
          {euro(data.acceptedThisMonthValueCents)}
        </div>
        <div className="text-xs text-muted-foreground">
          {data.acceptedThisMonthCount}{' '}
          {data.acceptedThisMonthCount === 1 ? 'offerte' : 'offertes'} ·{' '}
          {data.acceptedThisMonthFullySigned} volledig ondertekend
        </div>
        <MonthlyBars data={data} />
      </section>

      <div className="flex flex-col divide-y xl:w-[360px]">
        <StatRow
          label="Acceptatieratio"
          hint={
            data.decidedCount
              ? `${data.acceptedCount} getekend van ${data.decidedCount} afgeronde`
              : 'Nog geen afgeronde offertes'
          }
          value={data.winRate === null ? '—' : `${Math.round(data.winRate * 100)}%`}
        />
        <StatRow
          label="Gemiddelde offertewaarde"
          hint="Over de lopende offertes"
          value={euro(data.averageOpenValueCents)}
        />
        <StatRow
          label="Verloopt binnen 7 dagen"
          hint={
            expiring
              ? `${expiring.count} ${expiring.count === 1 ? 'offerte' : 'offertes'} · ${euro(expiring.valueCents)}`
              : 'Niets loopt af deze week'
          }
          value={String(expiring?.count ?? 0)}
          tone={expiring ? 'warning' : 'default'}
        />
      </div>
    </Card>
  );
}

export default PipelineSummary;
